/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {decryptId, encryptId} from '_metronic/helpers/Encrypt'
import {showMessage} from '_metronic/helpers/swalHelper'
import {useAppDispatch} from '../../../../redux/hooks'
import {SetCampus} from '../../../../redux/system/user/userSlice'

const initialValues = {
  username: localStorage.getItem('username') || '',
  password: localStorage.getItem('password') ? decryptId(localStorage.getItem('password')) : '',
  rememberMe: false,
}

export function Login() {
  const {t} = useTranslation()
  const loginSchema = Yup.object().shape({
    username: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.USERNAME')})),
    password: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.PASSWORD')})),
  })
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const [showCampusModal, setShowCampusModal] = useState(false)
  const [campuses, setCampuses] = useState<any>([])
  const dispatch = useAppDispatch()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.username, values.password)
        saveAuth(auth)
        if (auth.permissions?.includes('campus-multiple') || (auth?.system_id ?? 0) > 0) {
          // Temporarily save campuses and token
          setCampuses(auth?.campuses)
          setShowCampusModal(true)
          setLoading(false)
          return
        }
        const {data: user} = await getUserByToken(auth.api_token)
        setCurrentUser(user)
        if (values.rememberMe) {
          localStorage.setItem('username', values.username)
          localStorage.setItem('password', encryptId(values.password))
        }
      } catch (error: any) {
        if (error.response.data) {
          if (error.response.status === 404) {
            setStatus(t('MESSAGES.NOT_FOUND_ERROR'))
          } else {
            const msg = t('VALIDATION.' + error.response.data.message)
            setStatus(msg)
          }
        } else if (error.request) {
          console.log('request', error.request)
        } else {
          console.log('Error', error.message)
        }
        saveAuth(undefined)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleCampusSelect = async (campusId: any, type: any, systemId: any) => {
    const formData = new FormData()
    formData.append('campus_id', campusId)

    localStorage.setItem('system_id', systemId)
    localStorage.setItem('system', type)

    if (type === 'System') {
      window.location.href = '/system-mgt/home'
      return
    }

    dispatch(SetCampus(formData)).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setCurrentUser(res.user)
        setShowCampusModal(false)

        if (type === 'University') window.location.href = '/dua/home'
        if (type === 'School') window.location.href = '/dphs/home'
        if (type === 'Canteen') window.location.href = '/canteen/home'
      } else {
        showMessage(t('MESSAGES.SERVER_ERROR'), 'error')
      }
    })
  }

  const groupedCampuses = campuses.reduce((acc: any, campus: any) => {
    if (!acc[campus.type]) acc[campus.type] = []
    acc[campus.type].push(campus)
    return acc
  }, {})

  const campusTypeStyles: any = {
    University: {btn: 'btn-primary', icon: '🎓'},
    School: {btn: 'btn-success', icon: '🏫'},
    Block: {btn: 'btn-secondary', icon: '🏢'},
    Canteen: {btn: 'btn-info text-white', icon: '🍽️'},
    System: {btn: 'btn-dark', icon: '⚙️'},
  }

  const typeOrder = ['System', 'University', 'School', 'Block', 'Canteen']

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : (
          <div></div>
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='required'>{t('GLOBAL.USERNAME')}:</label>
          <input
            placeholder={t('GLOBAL.USERNAME')}
            {...formik.getFieldProps('username')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.username && formik.errors.username},
              {
                'is-valid': formik.touched.username && !formik.errors.username,
              }
            )}
            type='text'
            name='username'
            autoComplete='off'
          />
          {formik.touched.username && formik.errors.username && (
            <div className='text-danger'>{formik.errors.username}</div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='required'>{t('GLOBAL.PASSWORD')}:</label>
          <input
            type='password'
            placeholder={t('GLOBAL.PASSWORD')}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='text-danger'>{formik.errors.password}</div>
          )}
        </div>
        {/* end::Form group */}
        <div className='d-flex justify-content-between'>
          <label>
            <input
              type='checkbox'
              className='form-check-input mx-2'
              name='rememberMe'
              checked={formik.values.rememberMe}
              onChange={formik.handleChange}
            />
            {t('GLOBAL.REMEMBER_ME')}
          </label>
          <Link to='/forgot-password'>{t('GLOBAL.FORGOT_PASSWORD')}</Link>
        </div>
        {/* begin::Action */}
        <div className='d-grid py-4'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>{t('GLOBAL.LOGIN')}</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {t('GLOBAL.PLEASE_WAIT')}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>

      {showCampusModal && (
        <div
          className='modal show fade d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered modal-xl'>
            <div className='modal-content'>
              <div className='modal-header p-3 bg-primary'>
                <h5 className='modal-title text-white'>{t('Select Campus')}</h5>
              </div>

              <div className='modal-body' style={{maxHeight: '70vh', overflowY: 'auto'}}>
                {typeOrder.map((type) => {
                  const campusesOfType = groupedCampuses[type]
                  if (!campusesOfType) return null

                  const style = campusTypeStyles[type] || {}

                  return (
                    <div key={type} className='mb-4'>
                      {/* Section Title */}
                      <div className='d-flex align-items-center mb-2'>
                        <h6 className='mb-0'>
                          {style.icon} {type}
                        </h6>
                        <div className='flex-grow-1 border-bottom ms-2'></div>
                      </div>

                      {/* Buttons */}
                      <div className='d-flex flex-wrap gap-2'>
                        {campusesOfType.map((campus: any) => (
                          <button
                            key={campus.id}
                            className={`btn btn-sm fw-bold ${style.btn} mx-5`}
                            style={{
                              borderRadius: '20px',
                              padding: '6px 14px',
                            }}
                            onClick={() =>
                              handleCampusSelect(campus.id, campus.type, campus.system_id)
                            }
                          >
                            {campus.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
